"use client";

import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const statsData = [{
  value: "4.5+",
  label: "AVERAGE RATING"
}, {
  value: "20+",
  label: "YEARS SERVING JASPER"
}, {
  value: "15k+",
  label: "OIL CHANGES COMPLETED"
}, {
  value: "100%",
  label: "HONEST PRICING"
}];
export default function RealResultsForDelhiClinics() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223377" id="why-marion-county-trusts-us" className="relative py-24 lg:py-32 bg-background overflow-hidden !bg-[url(https://evident-starfish.10web.cloud/wp-content/uploads/2026/05/ChatGPT-Image-May-13-2026-12_23_21-PM.png)] !bg-[url(https://evident-starfish.10web.cloud/wp-content/uploads/2026/05/tire_tread_pattern_bg.webp)]">
      {}
      <div className="absolute inset-0 z-0 opacity-[0.08] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673119/img/tire_tread_pattern_bg.png)] bg-cover bg-center bg-no-repeat mix-blend-multiply" />

      {}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      <div className="absolute top-1/4 left-[-10%] w-[40%] h-[40%] bg-primary/10 rounded-lg blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-[-10%] w-[40%] h-[40%] bg-chart-2/10 rounded-lg blur-3xl pointer-events-none" />

      <div ref={ref} className={`container relative z-10 mx-auto px-3 md:px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-6 tracking-tight uppercase">
            Why Marion County Trusts Us
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default">
            Delivering premium automotive care and reliable service to the Jasper community for over two decades.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {statsData.map((stat, i) => <div data-index={i} key={i} className="h-full">
              <Card className="h-full bg-card/80 backdrop-blur-sm border-2 border-border shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 rounded-md">
                <CardContent className="flex flex-col items-center justify-center p-6 md:p-8 text-center h-full">
                  <span className="text-5xl md:text-6xl lg:text-8xl font-bold font-default text-primary mb-4 tracking-tighter drop-shadow-md">
                    {stat.value}
                  </span>
                  <span className="text-sm md:text-base text-chart-2 font-bold font-mono leading-snug uppercase tracking-widest">
                    {stat.label}
                  </span>
                </CardContent>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}